import { useMemo, useState } from "react";
import { PageHeader, useResource, friendlyTime } from "@/admin/shared";
import { Button, Field, Input, Select, Modal, StatusPill } from "@/admin/ui";
import CredentialModal from "@/admin/CredentialModal";
import DataTable from "@/admin/DataTable";
import { api, formatApiError } from "@/admin/api";

const STATUSES = ["invited", "active", "suspended", "removed"];

const empty = {
    name: "",
    email: "",
    phone: "",
    company_id: "",
    company_name: "",
    role: "sales",
    department: "",
    status: "active",
};

export default function Users() {
    const { data, loading, refresh, error } = useResource("/users");
    const companies = useResource("/companies");
    const roles = useResource("/roles");
    const departments = useResource("/departments");

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const [search, setSearch] = useState("");
    const [cred, setCred] = useState(null);
    const [pwMode, setPwMode] = useState("auto");
    const [pwValue, setPwValue] = useState("");

    const rows = useMemo(() => {
        const list = data || [];
        if (!search.trim()) return list;
        const q = search.toLowerCase();
        return list.filter(
            (u) =>
                (u.name || "").toLowerCase().includes(q) ||
                (u.email || "").toLowerCase().includes(q) ||
                (u.company_name || "").toLowerCase().includes(q),
        );
    }, [data, search]);

    function openCreate() {
        setEditing(null);
        setForm(empty);
        setPwMode("auto");
        setPwValue("");
        setErr("");
        setOpen(true);
    }
    function openEdit(u) {
        setEditing(u);
        setForm({ ...empty, ...u });
        setErr("");
        setOpen(true);
    }

    async function onSave(e) {
        e?.preventDefault?.();
        setBusy(true);
        setErr("");
        try {
            const c = (companies.data || []).find((x) => x.id === form.company_id);
            const payload = { ...form, company_name: c ? c.name : form.company_name };
            if (editing) {
                await api.patch(`/users/${editing.id}`, payload);
            } else {
                const r = await api.post("/users", {
                    ...payload,
                    password_mode: pwMode,
                    password: pwMode === "manual" ? pwValue : "",
                });
                setCred({ email: r.data.email, password: r.data.temp_password, roleLabel: r.data.role });
            }
            setOpen(false);
            await refresh();
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    async function onDelete(u) {
        if (!confirm(`Remove user "${u.name}"?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }

    async function toggleStatus(u, next) {
        try {
            await api.patch(`/users/${u.id}`, { status: next });
            await refresh();
        } catch (e) {
            alert(formatApiError(e));
        }
    }

    async function resetPassword(u) {
        try {
            const r = await api.post(`/users/${u.id}/reset-password`);
            setCred({ email: u.email, password: r.data.temp_password, roleLabel: u.role });
        } catch (e) {
            alert(formatApiError(e));
        }
    }

    const cols = [
        {
            key: "name",
            label: "User",
            render: (r) => (
                <div>
                    <div className="font-semibold text-black">{r.name}</div>
                    <div className="text-[12px] text-neutral-500">{r.email}</div>
                </div>
            ),
        },
        { key: "company_name", label: "Company", render: (r) => r.company_name || "—" },
        {
            key: "role",
            label: "Role",
            render: (r) => (
                <span className="text-[11px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2.5 py-1">
                    {r.role}
                </span>
            ),
        },
        { key: "department", label: "Department", render: (r) => r.department || "—" },
        { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
        {
            key: "last_login",
            label: "Last Login",
            render: (r) => <span className="text-[12px] text-neutral-500">{r.last_login ? friendlyTime(r.last_login) : "—"}</span>,
        },
        {
            key: "actions",
            label: "",
            render: (r) => (
                <div className="flex items-center justify-end gap-1.5">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(r)} data-testid={`user-edit-${r.id}`}>Edit</Button>
                    <Button size="sm" variant="subtle" onClick={() => resetPassword(r)} data-testid={`user-reset-${r.id}`}>Reset PW</Button>
                    {r.status !== "suspended" ? (
                        <Button size="sm" variant="subtle" onClick={() => toggleStatus(r, "suspended")}>Suspend</Button>
                    ) : (
                        <Button size="sm" variant="subtle" onClick={() => toggleStatus(r, "active")}>Activate</Button>
                    )}
                    <Button size="sm" variant="danger" onClick={() => onDelete(r)} data-testid={`user-delete-${r.id}`}>Remove</Button>
                </div>
            ),
            tdClass: "text-right",
        },
    ];

    return (
        <div data-testid="admin-users-page">
            <PageHeader
                title="Users"
                subtitle="Every human in the platform — invite, assign roles, and control access."
                action={
                    <div className="flex items-center gap-2">
                        <Input
                            data-testid="user-search"
                            placeholder="Search…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="max-w-[220px]"
                        />
                        <Button size="lg" onClick={openCreate} data-testid="user-create-btn">
                            + Add User
                        </Button>
                    </div>
                }
            />
            {error && (
                <div className="rounded-2xl border border-rose-200 bg-rose-50/60 text-rose-700 text-sm px-4 py-3 mb-6">
                    {error}
                </div>
            )}
            <DataTable
                cols={cols}
                rows={rows}
                rowKey={(r) => r.id}
                loading={loading}
                empty={{ title: "No users", hint: "Add your first user or send an invite." }}
            />

            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title={editing ? "Edit User" : "Add User"}
                size="lg"
                testid="user-modal"
                footer={
                    <>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        <Button onClick={onSave} disabled={busy} data-testid="user-save-btn">
                            {busy ? "Saving…" : editing ? "Save" : "Create"}
                        </Button>
                    </>
                }
            >
                <form onSubmit={onSave} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Name">
                        <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-field-name" />
                    </Field>
                    <Field label="Email">
                        <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-field-email" />
                    </Field>
                    <Field label="Phone">
                        <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                    </Field>
                    <Field label="Company">
                        <Select value={form.company_id} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                            <option value="">— None —</option>
                            {(companies.data || []).map((c) => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Role">
                        <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                            {(roles.data || []).map((r) => (
                                <option key={r.id} value={r.key}>{r.name}</option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Department">
                        <Select value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
                            <option value="">— None —</option>
                            {(departments.data || []).map((d) => (
                                <option key={d.id} value={d.name}>{d.name}</option>
                            ))}
                        </Select>
                    </Field>
                    <Field label="Status">
                        <Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            {STATUSES.map((s) => <option key={s}>{s}</option>)}
                        </Select>
                    </Field>
                    {!editing && (
                        <div className="sm:col-span-2 rounded-2xl border border-black/10 bg-neutral-50/60 p-4">
                            <div className="text-[11px] font-semibold tracking-[0.16em] uppercase text-neutral-500 mb-2">
                                Login Password
                            </div>
                            <div className="flex flex-wrap items-center gap-4">
                                <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                    <input
                                        type="radio"
                                        name="pw-mode"
                                        checked={pwMode === "auto"}
                                        onChange={() => setPwMode("auto")}
                                        data-testid="user-pw-mode-auto"
                                    />
                                    Auto-generate a strong password
                                </label>
                                <label className="inline-flex items-center gap-2 text-[13px] text-black cursor-pointer">
                                    <input
                                        type="radio"
                                        name="pw-mode"
                                        checked={pwMode === "manual"}
                                        onChange={() => setPwMode("manual")}
                                        data-testid="user-pw-mode-manual"
                                    />
                                    Set password manually
                                </label>
                            </div>
                            {pwMode === "manual" && (
                                <div className="mt-3">
                                    <Input
                                        type="text"
                                        placeholder="Min 8 chars, incl. a letter and a number"
                                        value={pwValue}
                                        onChange={(e) => setPwValue(e.target.value)}
                                        data-testid="user-pw-value"
                                    />
                                </div>
                            )}
                            <div className="mt-2 text-[12px] text-neutral-500">
                                The password is shown once after creation. The user must change it on first login.
                            </div>
                        </div>
                    )}
                    {err && (
                        <div className="sm:col-span-2 rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">
                            {err}
                        </div>
                    )}
                </form>
            </Modal>

            <CredentialModal
                open={!!cred}
                onClose={() => setCred(null)}
                email={cred?.email}
                password={cred?.password}
                roleLabel={cred?.roleLabel}
            />
        </div>
    );
}
